// PublicPagesDirectory — /e/:slug listing + bulk-unpublish action per
// spec 47. 4-state status pill (draft / live / scheduled / archived).
// Bulk unpublish requires a reason text (audited).
//
// Current state: index + filter pills + placeholder rows. Backing data
// arrives once /api/admin/public-pages endpoint lands.

import { useState } from 'react'
import { FilterPill } from '../components/FilterPill'

type Status = 'draft' | 'live' | 'scheduled' | 'archived'

const STATUS_LABELS: Record<Status, string> = {
  draft: 'Draft',
  live: 'Live',
  scheduled: 'Scheduled',
  archived: 'Archived',
}

const STATUS_STYLES: Record<Status, string> = {
  draft: 'bg-stone-100 text-stone-700',
  live: 'bg-emerald-50 text-emerald-800',
  scheduled: 'bg-amber-50 text-amber-900',
  archived: 'bg-stone-50 text-stone-500',
}

export function PublicPagesDirectory() {
  const [filter, setFilter] = useState<Status | 'all'>('all')

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-3xl mb-1">Public pages</h1>
        <p className="text-ink-soft">
          Every <code>/e/:slug</code> page across the programme. Bulk
          unpublish requires a reason and is audited.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <FilterPill
          label="All"
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        {(['live', 'scheduled', 'draft', 'archived'] as Status[]).map(s => (
          <FilterPill
            key={s}
            label={STATUS_LABELS[s]}
            active={filter === s}
            onClick={() => setFilter(s)}
          />
        ))}
      </div>

      <div className="bg-white border border-line rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone-600">
            <tr>
              <th className="text-left px-4 py-2">Event</th>
              <th className="text-left px-4 py-2">Slug</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">Last edit</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={5} className="px-4 py-8 text-center text-ink-soft">
                Public-pages data lands once the per-event public-page
                editor (spec 24) is wired. Showing zero rows for now.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <p className="text-xs text-stone-500">
        Pill legend:
        {(['live', 'scheduled', 'draft', 'archived'] as Status[]).map(s => (
          <span
            key={s}
            className={`ml-2 inline-block rounded-full px-2 py-0.5 ${STATUS_STYLES[s]}`}
          >
            {STATUS_LABELS[s]}
          </span>
        ))}
      </p>
    </div>
  )
}
